/**
 * Routing over the discovered feature manifests.
 *
 * G2.2: "THE URL IS THE STATE. Any screen, filter set, drill path and scenario is a
 * shareable, bookmarkable link — this is also what makes G3 embedding work."
 *
 * So the router is built from `src/features/registry.ts` and the search string is the
 * canonical home of filters, drill path and scenario. There is no route table in this
 * file: a chapter agent adds a manifest and its routes appear.
 *
 * No router library. `react-router` is not on the design/09 §10.1 register and adding it
 * would need G4 approval; the requirement here is a path match, a lazy component, a
 * breadcrumb trail and browser back/forward, which the History API provides directly.
 * Adopting a router later is a contained change because features declare paths as data
 * rather than as JSX.
 */

import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { features, routes, screenGuide, type ResolvedRoute } from '../../features/registry';
import { DOCUMENTATION_GUIDE_PARAM, DOCUMENTATION_PATH } from '../../features/foundations/documentation-sections';
import { EmptyState, Skeleton } from '../../primitives';
import {
  PAGE_CONTENT, PageActionSlotProvider, PageHeader, PageHeadlineSlotProvider, PageKickerProvider, Tour, takeTourRequest,
} from '../../planning';
import { GuideControls } from './GuideControls';
import { OverviewScreen } from './overview';
import { PROFILE_PATH, ProfileScreen } from './profile/ProfileScreen';
import { MY_WORK_PATH } from './my-work/my-work';
import { MyWorkScreen } from './my-work/MyWorkScreen';
import { ADMIN_USERS_PATH, ADMIN_USERS_REQUIRES, SEARCH_PARAM } from './admin/constants';
import { FEEDBACK_PATH, FEEDBACK_TRIAGE_PATH, FEEDBACK_TRIAGE_REQUIRES } from './feedback/constants';
import { MyReportsScreen } from './feedback/MyReportsScreen';
import { ReportScreenFrame } from './feedback/guides';
import { CapabilityRouteGate } from './CapabilityRouteGate';
import { RequiresGate } from './RequiresGate';

/**
 * The landing path. Held as a constant because three things must agree about it: the
 * router's own branch below, the not-found state's "home" action, and `e2e/routes.ts`,
 * which has always enumerated `/` as the shell's route under test and so has always
 * audited whatever `/` rendered.
 */
export const HOME_PATH = '/';

/**
 * The screen-anatomy reference (spec v6b, ADR 9013) — development builds only. The condition is
 * `FIXTURE_PREVIEW_BUILD`'s own expression, written here rather than imported so that Vite's
 * replacement makes it a literal `false` IN THIS MODULE in a production bundle: the minifier
 * drops the branch and the dynamic import with it, and production has no such route — not a
 * hidden one.
 */
export const ANATOMY_PATH = '/_anatomy';
/**
 * User Management (ADR 9014). Lazy, unlike the Profile: an administrator opens it rarely and
 * nobody else can open it at all, so it has no reason to be in the entry chunk.
 */
const UserManagementScreen = lazy(() => import('./admin/UserManagementScreen')
  .then((module) => ({ default: module.UserManagementScreen })));

/**
 * Report triage (bata-platform#686, ADR 9063): lazy for User Management's reason — the delivery team
 * opens it, nobody else can, and the drawer's form has no business in the entry chunk.
 */
const TriageScreen = lazy(() => import('./feedback/TriageScreen')
  .then((module) => ({ default: module.TriageScreen })));

const AnatomyScreen = import.meta.env.MODE !== 'production'
  ? lazy(() => import('./anatomy/AnatomyScreen').then((module) => ({ default: module.AnatomyScreen })))
  : null;

// ── URL state, per G2.2 ──────────────────────────────────────────────────────

export interface UrlState {
  readonly path: string;
  /** Filters, drill path, scenario and comparison base. G2.2: "Drill preserves context
   *  — filters, date range, scenario and comparison base survive the descent." */
  readonly params: Readonly<Record<string, string>>;
}

export function readUrl(): UrlState {
  if (typeof window === 'undefined') return { path: '/', params: {} };
  const params: Record<string, string> = {};
  new URLSearchParams(window.location.search).forEach((value, key) => { params[key] = value; });
  return { path: window.location.pathname, params };
}

export function writeUrl(state: UrlState, replace = false): void {
  const search = new URLSearchParams(state.params).toString();
  const url = search ? `${state.path}?${search}` : state.path;
  if (replace) window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
}

/**
 * Matches a declared path against the current pathname, supporting `:param` segments.
 * Returns the extracted parameters, or null.
 */
export function matchPath(declared: string, actual: string): Record<string, string> | null {
  const d = declared.split('/').filter(Boolean);
  const a = actual.split('/').filter(Boolean);
  if (d.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < d.length; i += 1) {
    const segment = d[i];
    const value = a[i];
    if (segment === undefined || value === undefined) return null;
    if (segment.startsWith(':')) params[segment.slice(1)] = decodeURIComponent(value);
    else if (segment !== value) return null;
  }
  return params;
}

export function resolveRoute(pathname: string): { route: ResolvedRoute; params: Record<string, string> } | null {
  // Static segments win over parameterised ones, so `/inseason/summary` is not captured
  // by `/inseason/:articleId`.
  const scored = [...routes].sort((a, b) => {
    const aParams = (a.path.match(/:/g) ?? []).length;
    const bParams = (b.path.match(/:/g) ?? []).length;
    return aParams - bParams;
  });
  for (const candidate of scored) {
    const params = matchPath(candidate.path, pathname);
    if (params) return { route: candidate, params };
  }
  return null;
}

// ── breadcrumbs, per G2.2 ────────────────────────────────────────────────────

export interface Breadcrumb {
  readonly labelKey: string;
  readonly to: string;
}

/** G2.2 — "the level path is a breadcrumb; the browser back button walks it". */
export function breadcrumbsFor(pathname: string): readonly Breadcrumb[] {
  const segments = pathname.split('/').filter(Boolean);
  const trail: Breadcrumb[] = [];
  let cursor = '';
  for (const segment of segments) {
    cursor += `/${segment}`;
    const match = resolveRoute(cursor);
    if (match) trail.push({ labelKey: match.route.route.titleKey, to: cursor });
  }
  return trail;
}

// ── the router ───────────────────────────────────────────────────────────────

const componentCache = new Map<string, React.LazyExoticComponent<React.ComponentType>>();

function componentFor(route: ResolvedRoute): React.LazyExoticComponent<React.ComponentType> {
  let cached = componentCache.get(route.path);
  if (!cached) {
    cached = lazy(route.route.component);
    componentCache.set(route.path, cached);
  }
  return cached;
}

export interface RouterProps {
  readonly url: UrlState;
  readonly onNavigate: (to: string, params?: Record<string, string>) => void;
  /**
   * Replace the query string without adding a history entry.
   *
   * Only the overview uses it, and only to put the run it resolved into the address bar.
   * A push there would make the back button walk a URL the planner never chose — see
   * `routes/overview/OverviewScreen.tsx`. It is a separate prop rather than a flag on
   * `onNavigate` because "navigate" and "record where you already are" are different acts
   * and a boolean at the call site would not say which one was meant.
   */
  readonly onReplaceParams: (patch: Record<string, string>) => void;
}

export function Router({ url, onNavigate, onReplaceParams }: RouterProps): React.JSX.Element {
  const { t } = useTranslation();
  const match = useMemo(() => resolveRoute(url.path), [url.path]);
  /** The header's action node, which a screen's `<PageAction>` portals into — audit v5b D-07. */
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  // The screen's headline sentence (spec v6b §3, ADR 9013) portals here, under the title —
  // the same pattern as the action slot, for the same reason: the header is the router's and
  // the data the sentence is computed from is the screen's.
  const [headlineSlot, setHeadlineSlot] = useState<HTMLElement | null>(null);
  // A screen that is one of several places in the rail narrows the chapter kicker to its own
  // («Prezzi · Parametri», brief part 2 §5) through `<PageKicker>`; null keeps the chapter.
  const [kicker, setKicker] = useState<string | null>(null);
  // The route whose guided tour is running (ADR 9044). Kept as the route's path rather than a
  // flag, so moving to another screen ends the tour without an effect having to notice.
  const [touring, setTouring] = useState<string | null>(null);
  const matchedPath = match?.route.path ?? null;
  useEffect(() => {
    // A tour Documentation asked for when it opened this screen — taken once.
    if (matchedPath !== null && takeTourRequest(matchedPath)) setTouring(matchedPath);
  }, [matchedPath]);
  const endTour = useCallback(() => setTouring(null), []);

  // Before the not-found branch, and NOT as a feature route. `/` belongs to no chapter, so
  // putting it in a manifest would have to give it a slug and an ADR block it has no claim
  // to, and `census:features` refuses an ADR in a chapter block whose directory does not
  // exist. It is also deliberately not lazy: this is the first screen after sign-in and a
  // skeleton flash is a worse first impression than the few kB it costs the entry chunk.
  if (url.path === HOME_PATH) {
    return (
      <section aria-label={t('home.title')} className="flex min-w-0 flex-col">
        {/* The Cockpit draws its own hero (brief part 1 §6): the title is the synthesis sentence,
            computed from the reads the screen makes, so the band cannot sit outside it. */}
        <OverviewScreen
          params={url.params}
          onNavigate={onNavigate}
          onReplaceParams={onReplaceParams}
        />
      </section>
    );
  }

  // The Profile — a shell route for the same reason as `/`: it belongs to no chapter (ADR 9012).
  if (AnatomyScreen !== null && url.path === ANATOMY_PATH) {
    return (
      <section aria-label={t('anatomy.title')} className="flex min-w-0 flex-col">
        <PageHeader
          kickerKey="anatomy.kicker"
          titleKey="anatomy.title"
          headline={<div ref={setHeadlineSlot} className="empty:hidden" />}
        />
        <div data-layout-content="" className={PAGE_CONTENT}>
          <PageHeadlineSlotProvider value={headlineSlot}>
            <Suspense fallback={<Skeleton rows={8} />}>
              <AnatomyScreen search={new URLSearchParams(url.params).toString()} />
            </Suspense>
          </PageHeadlineSlotProvider>
        </div>
      </section>
    );
  }

  if (url.path === PROFILE_PATH) {
    return (
      <section aria-label={t('profile.title')} className="flex min-w-0 flex-col">
        <PageHeader kickerKey="profile.kicker" titleKey="profile.title" />
        <div data-layout-content="" className={PAGE_CONTENT}>
          <ProfileScreen />
        </div>
      </section>
    );
  }

  // "My work" (bata-ui#166 W0-8, ADR 9022) — a shell route beside the Cockpit, for the same
  // reason as the Profile: it spans chapters and belongs to none.
  if (url.path === MY_WORK_PATH) {
    return (
      <section aria-label={t('myWork.title')} className="flex min-w-0 flex-col">
        <PageHeader kickerKey="myWork.kicker" titleKey="myWork.title" />
        <div data-layout-content="" className={PAGE_CONTENT}>
          <MyWorkScreen onNavigate={onNavigate} />
        </div>
      </section>
    );
  }

  // "My reports" (bata-platform#686, ADR 9063) — what this person has reported, and where it stands.
  // A shell route for the Profile's reason: it spans chapters and belongs to none. Its guide and
  // tour are drawn by its own frame (ADR 9071): Documentation reads only the chapters' manifests.
  if (url.path === FEEDBACK_PATH) {
    return (
      <ReportScreenFrame path={FEEDBACK_PATH} kickerKey="feedback.reports.kicker" titleKey="feedback.reports.title">
        <MyReportsScreen />
      </ReportScreenFrame>
    );
  }

  // Report triage — a shell route gated by `requires`, as User Management is: the delivery team's role.
  if (url.path === FEEDBACK_TRIAGE_PATH) {
    return (
      <ReportScreenFrame path={FEEDBACK_TRIAGE_PATH} kickerKey="feedback.triage.kicker" titleKey="feedback.triage.title">
        <RequiresGate requires={FEEDBACK_TRIAGE_REQUIRES}>
          <Suspense fallback={<Skeleton rows={8} />}>
            <TriageScreen />
          </Suspense>
        </RequiresGate>
      </ReportScreenFrame>
    );
  }

  // User Management — a shell route for the Profile's reason (ADR 9012), gated by `requires`
  // exactly as a chapter route is (ADR 9014).
  if (url.path === ADMIN_USERS_PATH) {
    const search = url.params[SEARCH_PARAM] ?? '';
    return (
      <section aria-label={t('admin.users.title')} className="flex min-w-0 flex-col">
        <PageHeader kickerKey="admin.users.kicker" titleKey="admin.users.title" />
        <div data-layout-content="" className={PAGE_CONTENT}>
          <RequiresGate requires={ADMIN_USERS_REQUIRES}>
            <Suspense fallback={<Skeleton rows={8} />}>
              <UserManagementScreen
                search={search}
                onSearch={(next) => onNavigate(ADMIN_USERS_PATH, next === '' ? {} : { [SEARCH_PARAM]: next })}
              />
            </Suspense>
          </RequiresGate>
        </div>
      </section>
    );
  }

  if (!match) {
    // G2 §2.6 — "Empty, loading, error and no-permission states designed for every
    // surface — not left to the framework default." A 404 is one of those surfaces.
    return (
      <EmptyState
        variant="empty"
        titleKey="state.empty.title"
        bodyKey="state.empty.body"
        // This action used to navigate to `/`, which matched no route and rendered this
        // same state again. It now reaches the chapter index.
        action={{ labelKey: 'nav.home', onClick: () => onNavigate(HOME_PATH) }}
      />
    );
  }

  const Component = componentFor(match.route);
  const { route } = match.route;

  // The chapter's own name, for the header's kicker. Resolved from the manifest rather
  // than restated by the screen: a screen that named its own chapter could disagree with
  // navigation, and there would be no way to tell which was wrong.
  const chapter = features.find((f) => f.slug === match.route.slug);
  const kickerKey = chapter?.navLabelKey ?? chapter?.titleKey;
  const guide = screenGuide(match.route.path);

  return (
    <section aria-label={t(route.titleKey)} className="flex min-w-0 flex-col">
      <PageHeader
        {...(kickerKey === undefined ? {} : { kickerKey })}
        {...(kicker === null ? {} : { kicker })}
        titleKey={route.titleKey}
        // Empty until a screen declares its action; `empty:hidden` so a page with none keeps
        // the title's full width.
        action={<div ref={setActionSlot} className="flex shrink-0 flex-col items-end gap-1 empty:hidden" />}
        headline={<div ref={setHeadlineSlot} className="empty:hidden" />}
        {...(guide === undefined ? {} : {
          guide: (
            <GuideControls
              onTour={() => setTouring(match.route.path)}
              onRead={() => onNavigate(DOCUMENTATION_PATH, { [DOCUMENTATION_GUIDE_PARAM]: match.route.path })}
            />
          ),
        })}
      />
      {guide !== undefined && touring === match.route.path && guide.tour.length > 0 && (
        <Tour steps={guide.tour} onClose={endTour} />
      )}
      {/*
        NOTHING HERE ABOUT DRILLING — audit v5 N-04. A terminal screen used to print "This is
        the finest level this screen shows…" under its title: the same sentence under three
        different titles, in the place the planner reads first, explaining none of them. The
        line under the title is the screen's headline, when it has one (ADR 9031). That rows open into
        nothing is shown by rows that do not open (no chevron, no pointer); G2 test f's
        reviewer reason (`route.drill.reason`) stays in the manifest, where it is reviewed.
      */}
      {/*
        The body's inset IS the page header's (`page.margin`), so a band's rule, a loose
        paragraph, a page-level empty state and the title above them all start at one edge. ADR
        9006 deferred this change because it moves every screen at once; audit v4 G-16 asked for
        it and the product owner confirmed it, and because `Panel` is one implementation the
        change is here and in `Panel`, not in thirty-seven screen files. See ADR 9006's
        amendment. The inset steps 32 · 40 · 48 with the screen and the box stops at 1760px,
        left-aligned (`PAGE_CONTENT`, brief part 3 §B1, ADR 9038).
      */}
      <div data-layout-content="" className={PAGE_CONTENT}>
        <PageActionSlotProvider value={actionSlot}>
          <PageHeadlineSlotProvider value={headlineSlot}>
          <PageKickerProvider value={setKicker}>
            {/* `requires` is read here, for every chapter route (ADR 9014). None declares one yet.
                `capability` too (ADR 9037): the platform's grant, its reason in the screen's place. */}
            <RequiresGate requires={route.requires}>
              <CapabilityRouteGate capability={route.capability}>
                <Suspense fallback={<Skeleton rows={8} />}>
                  <Component />
                </Suspense>
              </CapabilityRouteGate>
            </RequiresGate>
          </PageKickerProvider>
          </PageHeadlineSlotProvider>
        </PageActionSlotProvider>
      </div>
    </section>
  );
}

/** Hook wiring the History API to component state, so back/forward walk the drill path. */
export function useUrlState(): {
  url: UrlState;
  navigate: (to: string, params?: Record<string, string>) => void;
  replaceParams: (patch: Record<string, string>) => void;
} {
  const [url, setUrl] = useState<UrlState>(() => readUrl());

  useEffect(() => {
    const onPopState = () => setUrl(readUrl());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /**
   * Omitting `params` CARRIES the current query string; passing one replaces it, and
   * passing `{}` clears it deliberately.
   *
   * It used to default to `{}`, which meant every caller that navigated by path alone —
   * which is every caller: the chapter nav hands `onNavigate` a path and nothing else —
   * silently dropped the drill context on the way. G2 §2.2 is "drill preserves context";
   * a scoped screen reads its scope from the query string, so a nav click landed it on
   * its "no scope selected" state every time. ADR 0018 §4 records the same defect from
   * the other end: `drill.resident` is skipped as unmeasurable, because timing a drill
   * that lands on "no drill happened" reports a fast screen and tests nothing.
   *
   * `undefined` and `{}` have to mean different things for that to work, so the default
   * is gone rather than changed — a caller that means "clear the context" still says so.
   */
  const navigate = useCallback((to: string, params?: Record<string, string>) => {
    const next = { path: to, params: params ?? url.params };
    writeUrl(next);
    setUrl(next);
  }, [url.params]);

  /**
   * Record where the screen already is, rather than navigating to it.
   *
   * `history.replaceState` and a state update, so the address bar and the shell's own
   * `url` agree — writing one without the other is how a later `navigate()` silently
   * drops a parameter, which is the defect `navigate`'s own note above records from the
   * other end. No history entry is added: the planner did not choose this URL.
   *
   * Takes a patch, not a full replacement, and merges it functionally against whatever
   * `url.params` is at the moment this actually runs — not the caller's closed-over
   * `params`. Two auto-resolving effects (e.g. a run ID and an exit date) can both fire
   * from the same render; if each rebuilt `{ ...params, [key]: value }` from the same
   * stale `params`, the second call would silently clobber the first's key. Merging
   * inside the functional `setUrl` updater means each patch lands on top of the other's,
   * regardless of firing order.
   */
  const replaceParams = useCallback((patch: Record<string, string>) => {
    setUrl((current) => {
      const next = { path: current.path, params: { ...current.params, ...patch } };
      writeUrl(next, true);
      return next;
    });
  }, []);

  return { url, navigate, replaceParams };
}
